import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  bannersApi,
  type Banner,
  type BannerTargetApp,
  type GetBannersParams,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  Select,
  Pagination,
} from '@/components/common';
import {
  DataGrid,
  DateCell,
  makeBadgeCell,
  makeActionCell,
  RowNumberCell,
} from '@/components/grid';

type TargetFilter = BannerTargetApp | 'ALL';

const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

const LINK_TYPE_LABEL: Record<Banner['linkType'], string> = {
  NONE: '없음',
  INTERNAL: '앱 내 이동',
  EXTERNAL: '외부 링크',
};

export function BannerListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(10);
  const [appliedTarget, setAppliedTarget] = useState<TargetFilter>('ALL');

  const [localTarget, setLocalTarget] = useState<TargetFilter>('ALL');
  const [localSize, setLocalSize] = useState(10);

  const queryParams = useMemo<GetBannersParams>(
    () => ({ page: pageIndex, size, targetApp: appliedTarget }),
    [pageIndex, size, appliedTarget],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['banners', queryParams],
    queryFn: () => bannersApi.getBanners(queryParams),
    placeholderData: prev => prev,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  const toggleUse = useMutation({
    mutationFn: (row: Banner) =>
      bannersApi.updateBanner(row.id, { isUse: !row.isUse }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });

  const removeBanner = useMutation({
    mutationFn: (row: Banner) => bannersApi.deleteBanner(row.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });

  const handleSearch = () => {
    setPageIndex(0);
    setSize(localSize);
    setAppliedTarget(localTarget);
  };

  const handleReset = () => {
    setLocalTarget('ALL');
    setLocalSize(10);
    setPageIndex(0);
    setSize(10);
    setAppliedTarget('ALL');
  };

  const columns = useMemo<ColDef<Banner>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '대상앱',
        field: 'targetApp',
        width: 110,
        cellRenderer: makeBadgeCell({
          toneMap: { BUSINESS: 'info', GUARDIAN: 'success' },
          labelMap: { BUSINESS: '비즈니스앱', GUARDIAN: '보호자앱' },
        }),
      },
      {
        headerName: '배너',
        flex: 1.5,
        minWidth: 260,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<Banner>) => {
          const row = p.data;
          if (!row) return '-';
          return (
            <div className="flex h-full items-center gap-2">
              {row.fileUrl ? (
                <img
                  src={row.fileUrl}
                  alt={row.title ?? '배너 이미지'}
                  className="h-9 w-[76px] shrink-0 rounded object-cover ring-1 ring-gray-200"
                />
              ) : (
                // 텍스트-only AI 배너(무사진): 주조색 그라디언트 썸네일
                <div
                  className="flex h-9 w-[76px] shrink-0 items-center justify-center rounded text-[9px] font-bold text-white ring-1 ring-gray-200"
                  style={{
                    background: `linear-gradient(135deg, ${row.pointColor || '#FF9934'} 0%, #1f2937 100%)`,
                  }}
                  title="이미지 없이 문구만(오버레이)"
                >
                  AI
                </div>
              )}
              <span className="truncate" title={row.title ?? ''}>
                {row.title || '(제목 없음)'}
              </span>
            </div>
          );
        },
      },
      {
        headerName: '링크',
        width: 130,
        sortable: false,
        valueGetter: p => {
          const row = p.data;
          if (!row) return '';
          return row.linkType === 'NONE'
            ? LINK_TYPE_LABEL.NONE
            : `${LINK_TYPE_LABEL[row.linkType]}`;
        },
        cellRenderer: (p: ICellRendererParams<Banner>) => {
          const row = p.data;
          if (!row) return '-';
          if (row.linkType === 'NONE') {
            return <span className="text-gray-400">없음</span>;
          }
          return (
            <span title={row.linkUrl ?? ''}>
              {LINK_TYPE_LABEL[row.linkType]}
            </span>
          );
        },
      },
      {
        headerName: '노출 위치',
        width: 210,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<Banner>) => {
          const row = p.data;
          if (!row) return '-';
          if (!row.isLocationBased) {
            return <span className="text-gray-500">전역</span>;
          }
          const store =
            row.companyName ??
            (row.companySeq != null ? `#${row.companySeq}` : '매장 미지정');
          const region = row.companyRegion ? ` · ${row.companyRegion}` : '';
          const km = row.radiusKm ?? 10;
          return (
            <div className="text-xs leading-tight">
              <div className="font-medium text-gray-700">
                {store}
                <span className="text-gray-500">{region}</span>
              </div>
              <div className="text-gray-500">반경 {km}km</div>
            </div>
          );
        },
      },
      { headerName: '순서', field: 'sortOrder', width: 84 },
      {
        headerName: '효과(노출/클릭)',
        width: 150,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<Banner>) => {
          const row = p.data;
          if (!row) return '-';
          const imp = row.impressions ?? 0;
          const clk = row.clicks ?? 0;
          const ctr = imp > 0 ? Math.round((clk / imp) * 1000) / 10 : 0;
          return (
            <div className="text-xs leading-tight">
              <div>
                노출 {imp.toLocaleString()} · 클릭 {clk.toLocaleString()}
              </div>
              <div className="text-gray-500">CTR {ctr}%</div>
            </div>
          );
        },
      },
      {
        headerName: '사용',
        field: 'isUse',
        width: 90,
        cellRenderer: makeBadgeCell({
          toneMap: { true: 'success', false: 'neutral' },
          labelMap: { true: '사용중', false: '중지' },
        }),
      },
      {
        headerName: '게시 기간',
        width: 240,
        sortable: false,
        valueGetter: p => {
          const row = p.data;
          if (!row) return '';
          if (!row.displayStartAt && !row.displayEndAt) return '제한 없음';
          const fmt = (dt?: string | null) =>
            dt ? dt.slice(0, 16).replace('T', ' ') : '-';
          return `${fmt(row.displayStartAt)} ~ ${fmt(row.displayEndAt)}`;
        },
      },
      {
        headerName: '등록일',
        field: 'createdAt',
        width: 160,
        cellRenderer: DateCell,
      },
      {
        headerName: '관리',
        width: 200,
        sortable: false,
        cellRenderer: makeActionCell<Banner>([
          {
            label: '수정',
            onClick: row => navigate(`/banners/${row.id}/edit`),
          },
          {
            label: '사용',
            onClick: row => toggleUse.mutate(row),
            show: row => !row.isUse,
          },
          {
            label: '중지',
            onClick: row => toggleUse.mutate(row),
            show: row => row.isUse,
          },
          {
            label: '삭제',
            variant: 'danger',
            onClick: row => {
              if (
                window.confirm(
                  `배너를 삭제하시겠습니까?\n${row.title || `(배너 #${row.id})`}`,
                )
              ) {
                removeBanner.mutate(row);
              }
            },
          },
        ]),
      },
    ],
    [navigate, startIndex],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="홈 배너"
        subtitle="보호자앱 홈 상단 스와이프 배너를 관리합니다. (순서 오름차순 노출, 권장 이미지 750×352px)"
        right={
          <button
            type="button"
            onClick={() => navigate('/banners/new')}
            className="whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            배너 등록
          </button>
        }
      />

      <FilterBar onSubmit={handleSearch} onReset={handleReset}>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localTarget}
            onChange={e => setLocalTarget(e.target.value as TargetFilter)}
          >
            <option value="ALL">전체 대상앱</option>
            <option value="GUARDIAN">보호자앱</option>
            <option value="BUSINESS">비즈니스앱</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-28">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataGrid<Banner>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.id)}
        emptyMessage="등록된 배너가 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
